import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

interface UnderwaterPlotProps {
  /** Drawdown series (negative values) */
  underwater: number[];
  /** Start date */
  startDate: string;
  /** Max drawdown duration in days */
  maxDurationDays?: number;
  /** Chart height */
  height?: number;
}

/**
 * Underwater plot showing drawdown over time.
 * Visualizes how deep and long drawdowns were.
 */
export default function UnderwaterPlot({
  underwater,
  startDate,
  maxDurationDays,
  height = 200
}: UnderwaterPlotProps) {
  const data = useMemo(() => {
    const start = new Date(startDate);

    return underwater.map((dd, i) => {
      const date = new Date(start);
      date.setDate(date.getDate() + i);

      return {
        date: date.toISOString().split('T')[0],
        drawdown: dd * 100, // Convert to percentage
      };
    });
  }, [underwater, startDate]);

  // Find max drawdown
  const maxDrawdown = Math.min(...underwater);
  const maxDrawdownPct = maxDrawdown * 100;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload[0]) return null;

    return (
      <div className="bg-gray-800 border border-gray-700 rounded p-3 shadow-lg">
        <p className="text-gray-400 text-xs mb-1">{label}</p>
        <p className="text-sm text-loss font-medium tabular-nums">
          {payload[0].value.toFixed(2)}%
        </p>
      </div>
    );
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-white">Underwater Plot</h3>
        <div className="text-xs text-gray-400">
          Max DD: <span className="text-loss font-medium tabular-nums">{maxDrawdownPct.toFixed(2)}%</span>
          {maxDurationDays && (
            <span className="ml-2">
              Duration: <span className="text-white">{maxDurationDays} days</span>
            </span>
          )}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <defs>
            <linearGradient id="underwaterGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#E66100" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#E66100" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="date"
            stroke="#6B7280"
            tick={{ fill: '#6B7280', fontSize: 10 }}
            tickLine={{ stroke: '#6B7280' }}
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getFullYear().toString().slice(2)}`;
            }}
            interval="preserveStartEnd"
          />
          <YAxis
            stroke="#6B7280"
            tick={{ fill: '#6B7280', fontSize: 10 }}
            tickLine={{ stroke: '#6B7280' }}
            tickFormatter={(value) => `${value.toFixed(0)}%`}
            domain={[Math.floor(maxDrawdownPct * 1.1), 0]}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="#6B7280" />
          <Area
            type="monotone"
            dataKey="drawdown"
            stroke="#E66100"
            strokeWidth={1}
            fill="url(#underwaterGradient)"
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Drawdown severity legend */}
      <div className="mt-3 pt-3 border-t border-gray-800 flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-loss/20 border border-loss/40"></div>
          <span className="text-gray-400">&lt; 5% (Normal)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-loss/40 border border-loss/60"></div>
          <span className="text-gray-400">5-10% (Caution)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-loss/80 border border-loss"></div>
          <span className="text-gray-400">&gt; 10% (Severe)</span>
        </div>
      </div>
    </div>
  );
}
